import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { requestPasswordReset, resetPassword } from '../repository/authRepository';

const PasswordReset: React.FC = () => {
    const [searchParams] = useSearchParams();
    const resetToken = searchParams.get('token');
    const navigate = useNavigate();

    const [username, setUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState<string | null>(null);
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleRequestReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);

        const result = await requestPasswordReset({ username });

        if ('error' in result) {
            setMessage(result.error);
            setIsError(true);
        } else {
            setMessage('Password reset email sent! Check your inbox.');
            setIsError(false);
            setUsername('');
        }

        setLoading(false);
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            setMessage('Passwords do not match');
            setIsError(true);
            return;
        }

        if (newPassword.length < 8) {
            setMessage('Password must be at least 8 characters');
            setIsError(true);
            return;
        }

        if (!resetToken) {
            setMessage('No reset token provided');
            setIsError(true);
            return;
        }

        setLoading(true);
        setMessage(null);

        const result = await resetPassword({ reset_token: resetToken, new_password: newPassword });

        if ('error' in result) {
            setMessage(result.error);
            setIsError(true);
        } else {
            setMessage('Password updated! Redirecting to login...');
            setIsError(false);
            setTimeout(() => navigate('/login'), 2000);
        }

        setLoading(false);
    };

    if (resetToken) {
        return (
            <div className="auth-container">
                <h2>Set New Password</h2>
                <form onSubmit={handleResetPassword} className="auth-form">
                    {message && (
                        <div className={isError ? 'error-message' : 'success-message'}>
                            {message}
                        </div>
                    )}
                    <div className="form-group">
                        <label htmlFor="newPassword">New Password</label>
                        <input
                            type="password"
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            minLength={8}
                            disabled={loading}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            minLength={8}
                            disabled={loading}
                        />
                    </div>
                    <button type="submit" className="btn-primary" disabled={loading}>
                        {loading ? 'Updating...' : 'Update Password'}
                    </button>
                </form>
                <p className="auth-link">
                    <Link to="/login">Back to login</Link>
                </p>
            </div>
        );
    }

    return (
        <div className="auth-container">
            <h2>Reset Password</h2>
            <p className="auth-helper">Enter your username and we'll email you a reset link.</p>
            <form onSubmit={handleRequestReset} className="auth-form">
                {message && (
                    <div className={isError ? 'error-message' : 'success-message'}>
                        {message}
                    </div>
                )}
                <div className="form-group">
                    <label htmlFor="username">Username</label>
                    <input
                        type="text"
                        id="username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        disabled={loading}
                    />
                </div>
                <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
            </form>
            <p className="auth-link">
                <Link to="/login">Back to login</Link>
            </p>
        </div>
    );
};

export default PasswordReset;
